import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import _, { compact } from 'lodash';
import type {
  IDefaultAPIResult,
  IDefaultReducerState,
} from 'types/state.interface';
import { checkSavedDisabled } from 'utils/isSavedDisabled';
import type { TTimeRule } from '@timeedit/types/lib/types';
import { createEmptyTTimeRule } from '@timeedit/types/lib/utils';
import api from '../services/api.service';
import {
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
} from '../utils/sliceHelpers';
import { startAppListening } from './listenerMiddleware';
import type { TAppThunk, TRootState } from './store';

type TTimeRuleState = IDefaultReducerState & {
  allTimeRules: TTimeRule[];
  timeRuleMap: Record<number, TTimeRule>;
  firstTimeRule: TTimeRule | null;
  secondTimeRule: TTimeRule | null;
  selectedTimeRuleIds: number[];
};

export const initialState: TTimeRuleState = {
  loading: false,
  hasErrors: false,
  allTimeRules: [],
  timeRuleMap: {},
  firstTimeRule: null,
  secondTimeRule: null,
  selectedTimeRuleIds: [],
};

// Slice
const slice = createSlice({
  name: 'timeRules',
  initialState,
  reducers: {
    fetchTimeRulesRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchTimeRulesSuccess: (
      state,
      { payload }: PayloadAction<IDefaultAPIResult<TTimeRule>>
    ) => {
      state.allTimeRules = payload.results;
      state.timeRuleMap = _.keyBy(payload.results, 'id');
      finishedLoadingSuccess(state);
    },
    fetchTimeRulesFailure: (state) => {
      finishedLoadingFailure(state);
    },
    createTimeRuleRequest: (state) => {
      isLoadingRequest(state);
    },
    createTimeRuleSuccess: (state, { payload }: PayloadAction<TTimeRule>) => {
      state.allTimeRules.push(payload);
      finishedLoadingSuccess(state);
    },
    createTimeRuleFailure: (state) => {
      finishedLoadingFailure(state);
    },
    deleteTimeRuleRequest: (state) => {
      isLoadingRequest(state);
    },
    deleteTimeRuleSuccess: (
      state,
      { payload: timeRuleId }: PayloadAction<number>
    ) => {
      state.allTimeRules = state.allTimeRules.filter(
        (timeRule: TTimeRule) => timeRule.id !== timeRuleId
      );
      state.timeRuleMap[timeRuleId] = undefined;
      finishedLoadingSuccess(state);
    },
    deleteTimeRuleFailure: (state) => {
      finishedLoadingFailure(state);
    },
    updateTimeRuleRequest: (state) => {
      isLoadingRequest(state);
    },
    updateTimeRuleSuccess: (state, { payload }: PayloadAction<TTimeRule>) => {
      const index = state.allTimeRules.findIndex(
        (timeRule: TTimeRule) => timeRule.id === payload.id
      );
      state.allTimeRules[index] = payload;
      state.timeRuleMap[payload.id] = payload;
      finishedLoadingSuccess(state);
    },
    updateTimeRuleFailure: (state) => {
      finishedLoadingFailure(state);
    },
    setFirstTimeRule: (state, { payload }: PayloadAction<TTimeRule>) => {
      const prevItem: TTimeRule = state.firstTimeRule;
      state.firstTimeRule = payload;
      if (prevItem?.id === 0) {
        state.timeRuleMap[0] = undefined;
        state.allTimeRules = state.allTimeRules.filter(
          (ar: TTimeRule) => ar.id !== 0
        );
      }
    },
    setSecondTimeRule: (state, { payload }: PayloadAction<TTimeRule>) => {
      state.secondTimeRule = payload;
    },
    createEmptyTimeRule: (state) => {
      if (state.firstTimeRule?.id === 0) return;
      const newItem = createEmptyTTimeRule();
      state.firstTimeRule = newItem;
      state.timeRuleMap[newItem.id] = newItem;
      state.allTimeRules.unshift(newItem);
    },
    duplicateTimeRule: (state, { payload }: PayloadAction<TTimeRule>) => {
      if (state.firstTimeRule?.id === 0) return;
      const newItem: TTimeRule = {
        ...payload,
        id: 0,
        name: `${payload.name} (copy)`,
      };
      state.firstTimeRule = newItem;
      state.timeRuleMap[newItem.id] = newItem;
      state.allTimeRules.unshift(newItem);
    },
    changeTimeRule: <Key extends keyof TTimeRule>(
      state: TTimeRuleState,
      {
        payload,
      }: PayloadAction<{
        isFirstDrawer: boolean;
        key: Key;
        value: TTimeRule[Key];
        object?: Partial<TTimeRule>;
      }>
    ) => {
      const { isFirstDrawer, key, value, object } = payload;
      const changedObject = object || { [key]: value };
      if (isFirstDrawer) {
        state.firstTimeRule = {
          ...state.firstTimeRule,
          ...changedObject,
        };
      } else {
        state.secondTimeRule = {
          ...state.secondTimeRule,
          ...changedObject,
        };
      }
    },
    discardChanges: (
      state,
      {
        payload,
      }: PayloadAction<{
        isFirstDrawer: boolean;
        id: number;
      }>
    ) => {
      const { isFirstDrawer, id } = payload;
      if (isFirstDrawer) {
        state.firstTimeRule = state.timeRuleMap[id];
      } else {
        state.secondTimeRule = state.timeRuleMap[id];
      }
    },
    closeDrawer: (state, { payload: id }: PayloadAction<number>) => {
      if (state.firstTimeRule?.id === id) {
        state.firstTimeRule = null;
      }
      if (state.secondTimeRule?.id === id) {
        state.secondTimeRule = null;
      }
    },
    setSelectedTimeRuleIds: (state, { payload }: PayloadAction<number[]>) => {
      state.selectedTimeRuleIds = payload;
    },
  },
});

export default slice.reducer;

// Selectors
export const timeRulesSelector = (state: TRootState) =>
  state.timeRules.allTimeRules;
export const firstTimeRuleSelector = (state: TRootState) =>
  state.timeRules.firstTimeRule;
export const secondTimeRuleSelector = (state: TRootState) =>
  state.timeRules.secondTimeRule;
export const selectedTimeRuleIdsSelector = (state: TRootState) =>
  state.timeRules.selectedTimeRuleIds;
export const timeRuleMapSelector = (state: TRootState) =>
  state.timeRules.timeRuleMap;
export const timeRuleLoadingSelector = (state: TRootState) =>
  state.timeRules.loading;

// Actions
export const {
  fetchTimeRulesRequest,
  fetchTimeRulesSuccess,
  fetchTimeRulesFailure,
  createTimeRuleRequest,
  createTimeRuleSuccess,
  createTimeRuleFailure,
  deleteTimeRuleRequest,
  deleteTimeRuleSuccess,
  deleteTimeRuleFailure,
  updateTimeRuleRequest,
  updateTimeRuleSuccess,
  updateTimeRuleFailure,
  setFirstTimeRule,
  setSecondTimeRule,
  createEmptyTimeRule,
  duplicateTimeRule,
  changeTimeRule,
  discardChanges,
  closeDrawer,
  setSelectedTimeRuleIds,
} = slice.actions;

export const fetchTimeRules = (): TAppThunk => async (dispatch) => {
  try {
    dispatch(fetchTimeRulesRequest());
    const timeRules = await api.get({ endpoint: `/time-rules` });
    dispatch(fetchTimeRulesSuccess(timeRules));
  } catch (e) {
    dispatch(fetchTimeRulesFailure());
    return console.error(e);
  }
};

export const copyTimeRule =
  (timeRule: TTimeRule): TAppThunk =>
  async (dispatch) => {
    try {
      dispatch(createTimeRuleRequest());
      const response = { ...timeRule, id: 0, description: '' };
      dispatch(createTimeRuleSuccess(response));
    } catch (e) {
      dispatch(createTimeRuleFailure());
      return console.error(e.response.data ? e.response.data : e.response);
    }
  };

export const deleteTimeRule =
  (timeRuleId: number): TAppThunk =>
  async (dispatch) => {
    try {
      dispatch(deleteTimeRuleRequest());
      await api.delete({
        endpoint: `/time-rules/${timeRuleId}`,
      });
      dispatch(deleteTimeRuleSuccess(timeRuleId));
      dispatch(closeDrawer(timeRuleId));
    } catch (e) {
      dispatch(deleteTimeRuleFailure());
      return console.error(e);
    }
  };

export const saveTimeRule =
  (timeRule: TTimeRule): TAppThunk =>
  async (dispatch) => {
    try {
      dispatch(updateTimeRuleRequest());
      const {
        id,
        history,
        absoluteStartTime,
        absoluteEndTime,
        absoluteCancellationStartTime,
        absoluteCancellationEndTime,
        ...timeRuleBody
      } = timeRule;
      const data = {
        ...timeRuleBody,
        absoluteStartTime,
        absoluteEndTime:
          absoluteEndTime && absoluteStartTime === absoluteEndTime
            ? absoluteEndTime + 86399
            : absoluteEndTime,
        absoluteCancellationStartTime,
        absoluteCancellationEndTime:
          absoluteCancellationEndTime &&
          absoluteCancellationStartTime === absoluteCancellationEndTime
            ? absoluteCancellationEndTime + 86399
            : absoluteCancellationEndTime,
      };
      if (id !== 0) {
        const response = await api.patch({
          endpoint: `/time-rules/${id}`,
          data,
        });
        dispatch(updateTimeRuleSuccess(response));
      } else {
        await api.post({
          endpoint: `/time-rules`,
          data,
        });
        dispatch(fetchTimeRules());
      }
      dispatch(closeDrawer(id));
    } catch (e) {
      dispatch(updateTimeRuleFailure());
      return console.error(e.response.data ? e.response.data : e.response);
    }
  };

export const checkFirstTimeRuleExists =
  (): TAppThunk<boolean> => (_, getState) =>
    !!getState().timeRules.firstTimeRule;

export const checkTimeRuleSaveDisabled =
  (isFirstDrawer: boolean): TAppThunk<boolean> =>
  (_, getState) => {
    const { timeRules } = getState();
    const { firstTimeRule, secondTimeRule, timeRuleMap } = timeRules;
    if (isFirstDrawer && firstTimeRule && timeRuleMap[firstTimeRule.id]) {
      return checkSavedDisabled(firstTimeRule, timeRuleMap[firstTimeRule.id]);
    }
    if (!isFirstDrawer && secondTimeRule && timeRuleMap[secondTimeRule.id]) {
      return checkSavedDisabled(secondTimeRule, timeRuleMap[secondTimeRule.id]);
    }

    return true;
  };

startAppListening({
  predicate: (_, currentState, prevState) =>
    currentState.timeRules.firstTimeRule?.id !==
      prevState.timeRules.firstTimeRule?.id ||
    currentState.timeRules.secondTimeRule?.id !==
      prevState.timeRules.secondTimeRule?.id,
  effect: (_, { dispatch, getState }) => {
    const {
      timeRules: { firstTimeRule, secondTimeRule },
    } = getState();
    dispatch(
      setSelectedTimeRuleIds(compact([firstTimeRule?.id, secondTimeRule?.id]))
    );
  },
});
